
import {
  ChevronsUpDown,
  LogOut,
  Settings,
  CreditCard,
  Monitor,
  Sun,
  Moon,
} from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useSettingsStore } from '@/stores/useSettingsStore'
import { useTheme } from 'next-themes'
import React, { useState, useEffect } from 'react'

import { Avatar } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'

// Extracted to module level to prevent remounting on parent re-renders
interface ThemeSelectorProps {
  mounted: boolean
  theme: string | undefined
  setTheme: (theme: string) => void
}

const ThemeSelector: React.FC<ThemeSelectorProps> = ({ mounted, theme, setTheme }) => {
  if (!mounted) return null

  const themes = [
    { value: 'system', label: 'System Default', icon: Monitor },
    { value: 'light', label: 'Light Mode', icon: Sun },
    { value: 'dark', label: 'Dark Mode', icon: Moon },
  ]

  return (
    <div className="px-1 py-1.5">
      <div className="mb-2 text-xs font-medium text-muted-foreground">Theme</div>
      <div className="flex items-center gap-1">
        {themes.map(({ value, label, icon: ThemeIcon }) => (
          <button
            key={value}
            onClick={() => setTheme(value)}
            className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-accent ${
              theme === value
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title={label}
          >
            <ThemeIcon className="h-4 w-4" />
          </button>
        ))}
      </div>
    </div>
  )
}

interface UserInfoProps {
  user: { name: string; email: string; avatar?: string }
  initials: string
  showAvatar?: boolean
  showDetails?: boolean
}

const UserInfo: React.FC<UserInfoProps> = ({ user, initials, showAvatar = true, showDetails = true }) => (
  <>
    {showAvatar && (
      <Avatar
        src={user.avatar}
        alt={user.name}
        fallback={initials}
        size="sm"
        shape="square"
        className="rounded-lg"
      />
    )}
    {showDetails && (
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-semibold">{user.name}</span>
        <span className="truncate text-xs">{user.email}</span>
      </div>
    )}
  </>
)

export function NavUser({
  user,
  onLogout,
}: {
  user: {
    name: string
    email: string
    avatar?: string
  }
  onLogout?: () => void
}) {
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  // Handle hydration
  useEffect(() => {
    setMounted(true)
  }, [])

  const getInitials = (name: string) => {
    const words = name.trim().split(' ').filter(word => word.length > 0)
    if (words.length === 1) return words[0]![0]!.toUpperCase()
    return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase()
  }

  const initials = getInitials(user.name)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto w-full justify-start gap-2 p-2 hover:bg-accent"
        >
          <UserInfo user={user} initials={initials} />
          <ChevronsUpDown className="ml-auto size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-56 rounded-lg"
        side="right"
        align="end"
        sideOffset={8}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <UserInfo user={user} initials={initials} />
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ThemeSelector mounted={mounted} theme={theme} setTheme={setTheme} />
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => useSettingsStore.getState().open()}>
            <Settings className="mr-2 size-4" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => useSettingsStore.getState().open('billing')}>
            <CreditCard className="mr-2 size-4" />
            Billing
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout}>
          <LogOut className="mr-2 size-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
